/**
 * Plain-language explanations for Owen's sidebar (first person).
 * Options: question id → option value. Questions: question id → string or by loanPurpose.
 */
import { CREDIT_SCORE_MAX, CREDIT_SCORE_MIN } from '../utils/creditScoreInput';

const GENERIC_OPTION_HELP = "I'd choose this if it best describes your situation. If you're unsure, tap another help icon or pick the closest match — you can always change your answer.";

const GENERIC_QUESTION_HELP = "Share your best estimate here. This is for loan preparation only — not a credit decision. Tap help anytime if you're unsure what to enter.";

const QUESTION_HELP = {
  propertyLocation: "Start with the property ZIP code if you know it — I'll look up state and county. If you don't have the ZIP yet, you can enter state and county manually instead.",
  propertyCounty: "Some ZIP codes cross county lines. The county matters for local loan limits and program rules, so I need the one where the home actually sits.",
  estimatedValue: "This is your best estimate of the home's market value — purchase price if you're buying, or current value if you're refinancing. A rough number is fine for now.",
  downPayment: {
    purchase: "How much cash you plan to put toward the purchase at closing. This helps me estimate loan-to-value and which low-down-payment programs might fit.",
    refinance: "Enter the loan balance you want to pay off — your remaining principal on the existing mortgage, not a new down payment.",
  },
  creditScore: `Enter your estimated FICO-style credit score as three digits. I accept ${CREDIT_SCORE_MIN} through ${CREDIT_SCORE_MAX}. This is self-reported for preparation — I'm not pulling your credit.`,
  creditBand: "Pick the range that best matches your credit. If you know the exact number, enter it when I ask for a score — the acceptable range is 400 through 850.",
  annualIncome: "Your gross annual income before taxes — wages, salary, and other regular income you expect the lender to count. A reasonable estimate is enough for now.",
  monthlyDebts: "Monthly payments on debts that show on a credit report — car loans, student loans, credit cards, etc. Don't include utilities, rent, or the new housing payment yet.",
  purchasePrice: "Your best estimate of the purchase price for the home — not including closing costs. We'll pair this with your down payment next.",
};

const OPTION_HELP = {
  purchaseOrRefinance: {
    purchase: "You're buying a home and need a new mortgage to complete the purchase. I'll ask about price, down payment, and occupancy next.",
    refinance: "You already own the home and want to replace or adjust your existing mortgage — not buy a new property. I'll ask about your current loan and refinance goal next.",
  },
  refiGoal: {
    'rate-term': "You want a lower interest rate or monthly payment, without taking cash out of the home. This is the most common refinance goal.",
    'limited-cash-out': "You need a small amount of cash at closing — for example to cover closing costs — while keeping the loan close to your current balance.",
    'cash-out': "You want to borrow more than you still owe and receive the difference in cash, using equity you have built in the home.",
    'usda-streamline': "You already have a USDA rural housing loan on this property and want to refinance it with less paperwork.",
    'not-sure': "That is fine — I will ask a few more questions and help narrow down what fits.",
    'lower-payment': "You mainly want a lower monthly payment or interest rate — not a large cash withdrawal.",
  },
  propertyUse: {
    primary: "You'll live in this home as your main residence for most of the year. Many government and affordable programs require primary occupancy.",
    secondary: "This is a vacation or second home — not your everyday residence. Program options differ from a primary home.",
    investment: "You won't live there; it's a rental or investment property. Qualification and down payment rules are stricter than for a primary home.",
  },
  propertyType: {
    'single-family': "A detached house — one unit, not sharing walls with neighbors. This fits the widest range of loan programs.",
    condo: "A condominium unit in a shared building. Some programs have extra condo project approval requirements.",
    townhouse: "An attached home, often row-style. Many programs treat this like single-family if you own the structure and land.",
    'multi-family': "A property with two to four units. You may live in one unit or rent them out — programs and limits depend on occupancy.",
    sfr: "A detached single-family home — the best fit for the V1 conventional and FHA preparation paths.",
    other: "Condo, townhouse, or multi-unit — V1 currently targets detached single-family only.",
  },
  militaryService: {
    yes: "You may qualify for VA-backed loans — often with no down payment and no monthly mortgage insurance if you meet service requirements.",
    no: "We'll focus on conventional, FHA, USDA, and other non-VA paths that fit your situation.",
  },
  ruralProperty: {
    yes: "The property is likely in a USDA-eligible area. That can open USDA Guaranteed loans for purchase or refinance if other rules are met.",
    no: "The property is in an urban or ineligible area for USDA. I'll steer away from USDA programs for this address.",
    unsure: "No problem — I'll note the uncertainty. We may confirm eligibility later with an official USDA address check.",
  },
  existingUSDALoan: {
    yes: "Your current loan is USDA — that unlocks the USDA streamline refinance path if the property stays eligible.",
    no: "Your current loan isn't USDA, so a USDA streamline won't apply even if the area is rural.",
  },
  priorNADL: {
    yes: "You're refinancing an existing Native American Direct Loan — a specialized VA tribal housing program.",
    no: "This isn't a NADL refinance scenario — we'll look at other refinance programs instead.",
  },
  stateHfaParticipant: {
    yes: "A state or local affordable homeownership program is already part of your loan — for example down-payment help arranged through a housing agency. Only pick this if someone already enrolled you.",
    no: "You are not using a state or local affordable housing program. That is the right answer for most buyers unless a counselor or loan officer told you otherwise.",
  },
  tribalHousingPath: {
    none: "You are using a regular home loan, not a special tribal housing program.",
    section184: "Section 184 is a loan program for enrolled members of federally recognized tribes buying in eligible areas.",
    nadl: "This is for eligible veterans obtaining a VA direct loan on tribal trust land with tribal approval.",
  },
  affordableProgramInterest: {
    yes: "You want me to look at programs that may offer lower rates or down-payment help if your income is within limits for your area.",
    no: "You prefer to focus on standard loan types without income-based assistance programs.",
  },
  firstTimeHomeBuyer: {
    yes: "You haven't owned a home in the last few years (typical first-time buyer definition). That can help with affordable and HFA programs.",
    no: "You've owned before — some first-time-only programs won't apply, but many standard options still will.",
  },
  renovationNeeds: {
    none: "You don't need repair or renovation funds in the loan — a standard purchase or refinance structure is enough.",
    minor: "You want modest updates or repairs rolled into the loan — programs like FHA 203(k) Limited or similar may fit.",
    major: "You need significant renovation financing — full 203(k) or conventional renovation programs may be worth exploring.",
    'not-sure': "We can keep renovation programs on the table until you know the scope of work.",
  },
  goal: {
    purchase: "You're preparing to buy a home — I'll ask about price, down payment, and a few scope questions next.",
    refinance: "You're preparing to refinance — I'll learn about your current loan and what you want to change.",
  },
  primaryResidence: {
    yes: "You'll live there as your main home. V1 preparation paths in this version focus on owner-occupied loans.",
    no: "This isn't your primary residence. V1 currently targets primary homes — I may suggest a different path later.",
  },
  citizenship: {
    'us-citizen': "You're a U.S. citizen — you fit the citizenship scope for the V1 preparation flows.",
    'permanent-resident': "You're a permanent resident — you fit the V1 scope for citizens and permanent residents.",
    other: "V1 preparation is limited to citizens and permanent residents for now — I may route you to a handoff.",
  },
  incomeType: {
    w2: "Most of your income is from an employer (W-2). That matches the six V1 W-2 preparation flows well.",
    other: "You're self-employed or have non-W-2 income. V1 is W-2-only today — I may suggest other support later.",
  },
  currentLoanType: {
    fha: "Your current loan is FHA-insured. That affects streamline and refinance options I can suggest.",
    va: "Your current loan is a VA loan. VA streamline (IRRRL) may be relevant if you stay in VA.",
    conventional: "Your current loan is conventional (Fannie/Freddie). Standard rate-and-term or cash-out refi rules apply.",
    other: "You're not sure of the loan type — I'll keep options open until we know more.",
  },
};

function resolveQuestionHelpText(entry, loanPurpose) {
  if (!entry) return null;
  if (typeof entry === 'string') return entry;
  if (loanPurpose && entry[loanPurpose]) return entry[loanPurpose];
  return entry.purchase || entry.refinance || Object.values(entry)[0] || null;
}

/**
 * @param {object} params
 * @param {string} params.questionId
 * @param {object} [params.questionDef]
 * @param {string} [params.loanPurpose]
 * @returns {{ label: string, text: string }}
 */
export function getQuestionHelp({ questionId, questionDef, loanPurpose }) {
  const label = questionDef?.question || 'This question';

  if (questionDef?.fieldHelp) {
    return { label: 'About this question', text: questionDef.fieldHelp };
  }

  const fromCatalog = resolveQuestionHelpText(QUESTION_HELP[questionId], loanPurpose);
  if (fromCatalog) {
    return { label: 'About this question', text: fromCatalog };
  }

  if (questionDef?.education) {
    const edu = questionDef.education;
    const parts = [edu.description, ...(edu.details || []).slice(0, 2)].filter(Boolean);
    if (parts.length) {
      return { label: edu.title || 'About this question', text: parts.join(' ') };
    }
  }

  return { label: 'About this question', text: GENERIC_QUESTION_HELP };
}

/**
 * @param {object} params
 * @param {string} params.questionId
 * @param {string} params.optionValue
 * @param {string} params.optionLabel
 * @param {object} [params.questionDef]
 * @returns {{ label: string, text: string }}
 */
export function getOptionHelp({ questionId, optionValue, optionLabel, questionDef }) {
  const label = optionLabel || String(optionValue);

  const fromOption = questionDef?.options?.find((o) => o.value === optionValue)?.help;
  if (fromOption) {
    return { label, text: fromOption };
  }

  const fromCatalog = OPTION_HELP[questionId]?.[optionValue];
  if (fromCatalog) {
    return { label, text: fromCatalog };
  }

  if (questionDef?.education?.details?.length) {
    return {
      label,
      text: `For "${label}": ${questionDef.education.details[0]} ${GENERIC_OPTION_HELP}`,
    };
  }

  return {
    label,
    text: GENERIC_OPTION_HELP,
  };
}

export function hasOptionHelp({ questionId, optionValue, questionDef }) {
  if (questionDef?.options?.find((o) => o.value === optionValue)?.help) return true;
  if (OPTION_HELP[questionId]?.[optionValue]) return true;
  return Boolean(questionDef?.education);
}
